import {
  Component,
  computed,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NgZorroModule } from '../../../shared/modules/ng-zorro/ng-zorro.module';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ListTableComponent } from '../../../shared/components/list-table/list-table.component';
import { DropdownSelectComponent } from '../../../shared/components/dropdown-select/dropdown-select.component';
import {
  TableRowAction,
  TableRowActionsModel,
  ColumnType,
} from '../../../shared/interfaces/list.interface';
import { TableRowActions } from '../../../shared/interfaces/actions';
import { DateTimeFormatterService } from '../../../shared/utils/date-time-formatter.service';
import {
  SearchProvidersDetailsItem,
  SearchProvidersPayload,
  SearchProvidersResponse,
} from '../../../shared/interfaces/provider.model';
import { LeadDetailsService } from '../../../services/lead-details.service';
import { ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS, STATIC_DATA } from '../../../shared/constants/static-data.constants';
import { SectionsService } from '../../../services/sections.service';
import { UserService } from '../../../services/user.service';
import { StorageService } from '../../../services/storage.service';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';

@Component({
  selector: 'ppl-provider-search-and-select',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    NzDatePickerModule,
    NgZorroModule,
    ButtonComponent,
    ListTableComponent,
    DatePickerComponent,
    DropdownSelectComponent
  ],
  templateUrl: './provider-search-and-select.component.html',
  styleUrl: './provider-search-and-select.component.css',
})
export class ProviderSearchAndSelectComponent implements OnInit {
  private dateTimeFormatterSvc = inject(DateTimeFormatterService);
  private leadDetailsSvc = inject(LeadDetailsService);
  @Input() providerType!: string;
  @Input() relationshipOptions: any[] = [];
  @Output() cancel = new EventEmitter<void>();
  @Output() providerSelected = new EventEmitter<SearchProvidersDetailsItem>();
  @Output() addingNewProvider = new EventEmitter<void>();

  providers: WritableSignal<SearchProvidersDetailsItem[] | null> = signal(null);

  showResultsList: Signal<boolean> = computed(() => {
    return this.providers() === null ? false : this.providers()?.length != 0;
  });

  program: any;

  selectedProviderForRelation: any = null;
  selectedRelation: any = null;
  relationError: string | null = null;

  searchQueryFormGroup = new FormGroup({
    fname: new FormControl(''),
    lname: new FormControl(''),
    dob: new FormControl<Date | string>(''),
    pplId: new FormControl(''),
    providerType: new FormControl(''),
    selectedProgramCode: new FormControl(''),
    ssn: new FormControl('')
  },
  { validators: this.pplIdOrSsnValidator() });

  actionButtons: TableRowActionsModel = new TableRowActions({ add: true });

  cols: ColumnType[] = [
    {
      header: 'PPL ID',
      field: 'providerPPLId',
    },
    {
      header: 'First Name',
      field: 'firstName',
    },
    {
      header: 'Last Name',
      field: 'lastName',
    },
    {
      header: 'Date of Birth',
      field: 'dob',
    },
  ];

  constructor(private sectionsService: SectionsService, private userService: UserService, private storageService: StorageService){
  }

  ngOnInit(): void {
    this.program = this.userService.getSelectedProgram();
    this.initForm();
  }

  pplIdOrSsnValidator(): (control: AbstractControl) => ValidationErrors | null {
    return (control: AbstractControl): ValidationErrors | null => {
      const pplId = control.get('pplId')?.value;
      const ssn = control.get('ssn')?.value;

      return (pplId || ssn) ? null : { pplIdOrSsnOrDobRequired: true };
    };
  }

  initForm() {
    if (this.providerType) {
      this.searchQueryFormGroup.controls['providerType'].setValue(
        this.providerType
      );
    }
    if (this.program && this.program.program_code) {
      this.searchQueryFormGroup.controls['selectedProgramCode'].setValue(
        this.program.program_code
      );
    }
  }

  action(event: any) {
    if (event.action === TableRowAction.Add) {
      this.selectedProviderForRelation = event.data;
      this.selectedRelation = null;
      this.relationError = null;
    }
  }

  saveRelation() {
    if (!this.selectedRelation) {
      this.relationError = 'Please select a relationship to consumer.';
      return;
    }
    this.relationError = null;
    this.handleAddProvider(this.selectedProviderForRelation);
  }

  limitDigits(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if(value.length > 9) {
      value = value.slice(0,9);
    }

    event.target.value = value;
    this.searchQueryFormGroup.get('ssn')?.setValue(value);
  }

  handleAddProvider(provider: any) {
    // this.providerSelected.emit(provider);
    const enrollmentTypes = this.sectionsService.getEnrollmentType();
    const fieldName = enrollmentTypes && enrollmentTypes.length > 0 ? enrollmentTypes[0] : "provider";
    const id = this.storageService.getParticipantId() || this.userService.getSelectedLead();
    const payload = {
      programCode: provider.programCode,
      providerId: provider.providerId,
      participantId: id,
      pageType: 'participantDetail',
      providerType: ENROLLMENT_TYPE_UI_TO_BACKEND_MAPPINGS[fieldName],
      ...(this.selectedRelation ? { relationToParticipant: this.selectedRelation } : {}),
    };
    this.leadDetailsSvc
      .addParticipantProviderAssociation(payload, true)
      .subscribe((data: any) => {
        if (data.status == 200) {
          this.selectedProviderForRelation = null;
          this.selectedRelation = null;
          this.cancel.emit();
        }
      });
  }

  search() {
    if (this.searchQueryFormGroup.invalid) {
      // If the form is invalid, do not proceed with the search
      return;
    }
    const formGroupValue = { ...this.searchQueryFormGroup.value };
    const dob = formGroupValue.dob;
    const query = {
      firstName: formGroupValue.fname,
      lastName: formGroupValue.lname,
      providerPPLId: formGroupValue.pplId,
      dob: dob
        ? this.dateTimeFormatterSvc.formatDateToUSDateString(dob as Date)
        : '',
      providerType: formGroupValue.providerType,
      programCode: formGroupValue.selectedProgramCode,
      ssn: formGroupValue.ssn
    } as SearchProvidersPayload;
    this.leadDetailsSvc.searchProvidersByQuery(query).subscribe({
      next: (res: any) => {
        const searchRes = res as SearchProvidersResponse;
        if (
          !searchRes.responsedata ||
          searchRes.responsedata.providerDetails.length === 0
        ) {
          this.providers.set([]);
          return;
        }
        this.providers.set([
          ...(searchRes?.responsedata?.providerDetails ?? null),
        ]);
      },
    });
  }
}
